import { get, isEmpty } from 'lodash';
import template from 'url-template';

import {
    FETCH_HEALTH_CHECKS,
    SET_CARD_SCORES,
    SET_NODE_SCORE,
    SET_SUBSCRIPTION
}  from '../actions/actionTypes';

import { setBobLoading } from './bobActions';
import { setProcessId } from './reportingActions';
import { handleNetworkError, enqueueSnackbar } from './applicationMessageActions';
import { setSvcRequestRaw } from './svcRequestActions';

import awsHeader from '../helpers/awsAuth';
import lambdaBaseApi from '../apis/lambda';

const SubscriptionUrl = template.parse('/subscriptions');
const HealthChecksSummariesUrl = template.parse('/healthchecks/summaries');
const HealthChecksUrl = template.parse('/healthchecks{?companyId,clientId,campusId,siteId,areaId,systemId,equipmentId,page,pagesize,issueTypeId,priority,export}');

const PAGE = 0;
const PAGESIZE = 100;

export const createSubscription = (body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await lambdaBaseApi.post(
            SubscriptionUrl.expand(),
            body,
            header
        );

        dispatch(enqueueSnackbar('Subscription created successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const exportNodeHealthChecks = (node, options = {}, typeFilters, priorityFilters, statusFilters) => async (dispatch) => {
    const header = await awsHeader();
    const { type } = node;

    options.export = true;

    dispatch(enqueueSnackbar('Data export initialized', 'success'));

    try {
        switch (type) {
            case 'company':
                options.companyId = node.id;
                break;
            case 'client':
                options.clientId = node.id;
                break;
            case 'site':
                options.siteId = node.id;
                break;
            case 'campus':
                options.campusId = node.id;
                break;
            case 'areaItem':
                options.areaId = node.id;
                break;
            case 'equipmentItem':
                options.equipmentId = node.id;
                break;
            case 'systemItem':
                options.systemId = node.id;
                break;
            default:
                break;
        }

        let url =  HealthChecksUrl.expand({
            ...options
        });

        if (!isEmpty(priorityFilters)) {
            priorityFilters.forEach(priority => {
                url = `${url}&priority=${priority}`;
            });
        }

        if (!isEmpty(typeFilters)) {
            typeFilters.forEach(issueTypeId => {
                url = `${url}&issueTypeId=${issueTypeId}`;
            });
        }

        if (!isEmpty(statusFilters)) {
            statusFilters.forEach(status => {
                url = `${url}&status=${status}`;
            });
        }
        
        const response = await lambdaBaseApi.get(
            url,
            header
        );
        
        const processId = get(response, 'data.data', []);
        dispatch(setProcessId(processId));

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchNodeHealthChecks = (node, options = {}, typeFilters, priorityFilters, statusFilters) => async (dispatch) => {
    const header = await awsHeader();
    const { type } = node;

    options.page || (options.page = PAGE);
    options.pagesize || (options.pagesize = PAGESIZE);

    let meta = {
        message:    '',
        totalCount: 0
    };

    dispatch(setBobLoading('FetchHealthChecks'));
    try {
        switch (type) {
            case 'company':
                options.companyId = node.id;
                break;
            case 'client':
                options.clientId = node.id;
                break;
            case 'site':
                options.siteId = node.id;
                break;
            case 'campus':
                options.campusId = node.id;
                break;
            case 'areaItem':
                options.areaId = node.id;
                break;
            case 'equipmentItem':
                options.equipmentId = node.id;
                break;
            case 'systemItem':
                options.systemId = node.id;
                break;
            default:
                break;
        }

        let url =  HealthChecksUrl.expand({
            ...options
        });

        if (!isEmpty(priorityFilters)) {
            priorityFilters.forEach(priority => {
                url = `${url}&priority=${priority}`;
            });
        }

        if (!isEmpty(typeFilters)) {
            typeFilters.forEach(issueTypeId => {
                url = `${url}&issueTypeId=${issueTypeId}`;
            });
        }

        if (!isEmpty(statusFilters)) {
            statusFilters.forEach(status => {
                url = `${url}&status=${status}`;
            });
        }
        
        const response = await lambdaBaseApi.get(
            url,
            header
        );

        const payload = get(response, 'data.data', []);
        const updatedMeta = get(response, 'data.meta', {});
        meta = { ...meta, ...updatedMeta };

        dispatch({
            type:    FETCH_HEALTH_CHECKS,
            payload: {
                data: [ ...payload ],
                meta: { ...meta }
            }
        });
        dispatch(setBobLoading('FetchHealthChecks'));
    } catch (error) {
        dispatch(setBobLoading('FetchHealthChecks'));

        const updatedMeta = { ...meta, message: 'Failed to load' };
        dispatch({
            type:    FETCH_HEALTH_CHECKS,
            payload: {
                data: [],
                meta: { ...updatedMeta }
            }
        });
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const clearHealthChecks = () => async (dispatch, getState) => {
    dispatch({
        type:    FETCH_HEALTH_CHECKS,
        payload: {
            data: [],
            meta: {}
        }
    });
};

export const fetchFaultScores = ids => async (dispatch, getState) => {
    const header = await awsHeader();

    const body = {
        ids
    };

    if (isEmpty(ids)) {
        return [];
    }

    try {
        const response = await lambdaBaseApi.post(
            HealthChecksSummariesUrl.expand(),
            body,
            header
        );

        const payload = get(response, 'data.data', []);

        return payload;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }

};

export const getCardFaultScores = (nodeId) => async (dispatch, getState) => {
    const header = await awsHeader();

    const body = {
        ids: [nodeId]
    };

    try {
        const response = await lambdaBaseApi.post(
            HealthChecksSummariesUrl.expand(),
            body,
            header
        );

        const payload = get(response, 'data.data[0]', []);

        dispatch({
            type: SET_CARD_SCORES,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const getNodeFaultScore = (nodeId) => async (dispatch, getState) => {
    const header = await awsHeader();

    const body = {
        ids: [nodeId]
    };

    try {
        const response = await lambdaBaseApi.post(
            HealthChecksSummariesUrl.expand(),
            body,
            header
        );

        const data = get(response, 'data.data[0]', []);

        let payload = 'low';
        if (Number(data.high)) {
            payload = 'high';
        } else if (Number(data.medium)) {
            payload = 'medium';
        }

        dispatch({
            type: SET_NODE_SCORE,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const prepareServiceCallNode = rawNode => async (dispatch, getState) => {
    // grab node parents so that data sourcing from the tree looks similar
    // to data sourced from the Rules (on the Health Monitor table)
    const parents = _getParents(rawNode.parent, rawNode.type, rawNode.id);

    // assign default values
    const targets = {
        areas:       [],
        equipmentId: '00000000-0000-0000-0000-000000000000',
        systemId:    '00000000-0000-0000-0000-000000000000'
    };

    switch (rawNode.type) {
        case 'areaItem':
            // rules handle areas in an array, so we will do the same here
            targets.areas.push(rawNode.id);
            break;
        case 'equipmentItem':
            targets.equipmentId = rawNode.id;
            break;
        case 'systemItem':
            targets.systemId = rawNode.id;
            break;
        default:
            break;
    }

    const node = {
        type: rawNode.type,
        name: rawNode.name,
        id:   rawNode.id,
        ...parents,
        ...targets
    };

    // dispatch action to set raw service request node
    dispatch(setSvcRequestRaw(node));
};

export const setSubscription = payload => ({
    type: SET_SUBSCRIPTION,
    payload
});

const _getParents = (parents, nodeType, id) => {
    const leafNodes = ['areaItem', 'equipmentItem', 'systemItem'];
    const parentCollection = parents.split('/');
    const result = {
        clientId: '00000000-0000-0000-0000-000000000000',
        campusId: '00000000-0000-0000-0000-000000000000',
        siteId:   '00000000-0000-0000-0000-000000000000',
    };

    if (nodeType === 'company') {
        result.companyId = id;
    } else {
        result.companyId = parentCollection[0];
    }

    if (parentCollection.length >= 2) {
        result.clientId = parentCollection[1];
    }

    if (nodeType === 'site' && parentCollection.length === 3) {
        result.campusId = parentCollection[2];
    }

    if (leafNodes.includes(nodeType)) {
        if (parentCollection.length === 5) {
            result.campusId = parentCollection[2];
            result.siteId = parentCollection[3];
        } else if (parentCollection.length === 4) {
            result.siteId = parentCollection[2];
        }
    }

    return result;
};
